import React, { useState, useEffect } from "react";
import { uploadContent, deleteContentById } from "../../services/content-service";
import { useAuth0 } from "@auth0/auth0-react";
import "../../styles/components/content.css"
import Loader from "../navigation/Loader";
import toast from 'react-hot-toast';
import { CreateContactField } from "../contacts/create-contact-field";
import ContactCheckboxList from "./contact-checkbox-list";
import { getAllContacts, getContactAffiliations } from "../../services/contact-service";
import { AreYouSureModal } from "../shared/are-you-sure";
import { getFileExtensionFromFileName } from "../shared/utilities";
import Select from "react-select";
import { contentCategories } from "../../utils/content-utils";
import { useStandardQuery } from "../hooks/useStandardQuery";
import CustomOption from "../shared/react-select-option-check";
import CustomValueContainer from "../shared/react-select-custom-value-container";


export default function ContentCreateEdit({content = null}) {
    const [file, uploadFile] = useState(null); 
    const [fileName, setFileName] = useState(initialContentState("description")); 
    const [fileType, setFileType] = useState(initialContentState("fileType"));
    const [tags, setTags] = useState(initialContentState("tags"));
    const [inputTag, changeTagInput] = useState("");
    const [isSubmitting, setSubmitting] = useState(false);
    const [isDeleting, setDeleting] = useState(false);
    const [featureConfigs, setFeatureConfigs] = useState(initialContentState("featureConfigs"));
    const [isVisible, setisVisible] = useState(false);
    const [fullContactList, setFullContactList] = useState([]);
    const [contactsForEmail, setContactsForEmail] = useState(initialContentState("contactsForEmail"));
    const [showModal, setShowModal] = useState(false);
    const [contentCategory, setContentCategory] = useState(initialContentState("category"));
    const [allowedAffiliations, setAllowedAffiliations] = useState(initialContentState("allowedAffiliations"));
    const affiliations = useStandardQuery(getContactAffiliations, null);
    const contactsList = useStandardQuery(getAllContacts, null);

    const id = content?.id ?? null;
    const contactCountToSendEmail = contactsForEmail?.filter((element) => element.checked !== false)?.length;

    const editMode = content != null;

    function initialContentState(propName) {
        if (content) {
            if (propName === "featureConfigs") {
                return {
                    isFeatured: content["isFeatured"],
                    isBanner: content["isBanner"]
                }
            } else {
                return content[propName];
            } 
        }
        else {
            const initialStateNewContent = {
                id: null,
                file: null,
                fileType: null,
                description: "",
                tags: [],
                contactsForEmail: [],
                featureConfigs: {
                    isFeatured: false,
                    isBanner: false
                },
                category: "",
                allowedAffiliations: []
            }
            return initialStateNewContent[propName];
        }        
    }

    
    const { getAccessTokenSilently } = useAuth0();

    const resetState = () => {
        uploadFile(null);
        setFileName("");
        setTags([]);
        changeTagInput("");
        setSubmitting(false);
        setFeatureConfigs({isFeatured : false, isBanner: false});
        setisVisible(false);
        setAllowedAffiliations([]);
    }

    useEffect(() => {
        if (contactsList.status == "success") {
            //handle if affiliations are already selected
            const newContacts = contactsList.data.map(c => ({...c, checked: false}));
            setFullContactList(newContacts);
            if (allowedAffiliations?.length > 0) {
                const filteredContacts = newContacts.filter(c => allowedAffiliations?.some(a => c.affiliation?.id === a.id));
                setContactsForEmail(filteredContacts.map(c => ({...c, checked: false})));
            }
            else {
                setContactsForEmail(newContacts.map(c => ({...c, checked: false})));
            }
        }
    }, [contactsList.status])

    const handleFileChange = (event) => {
        uploadFile(event.target.files[0]);
        setFileName(event.target.files[0].name);
        const fileExtension = getFileExtensionFromFileName(event.target.files[0].name);
        setFileType(fileExtension);
    }

    function handleOtherFieldChange(e) {
        const propertyName = e.target.name;
        const propertyValue = e.target.type == "checkbox" ? e.target.checked : e.target.value;
        const newFeatureConfigs = {
            ...featureConfigs
        };
        newFeatureConfigs[propertyName] = propertyValue;
        setFeatureConfigs(newFeatureConfigs);
    }

    function handleAffiliationChange(newAffiliations) {
        newAffiliations = newAffiliations?.map(a => 
            ({id: a.value, name: a.label})
        );
        setAllowedAffiliations(newAffiliations);

        // update the contactsForEmail list to only include those that are in the selected affiliations
        const filteredContacts = newAffiliations.length == 0 ? fullContactList : 
            fullContactList.filter(c => newAffiliations?.some(a => c.affiliation?.id === a.id));
        setContactsForEmail(filteredContacts.map(c => 
            ({...c, checked: false})
        ));
    }

    const handleFileNameChange = (event) => setFileName(event.target.value);

    const handleChangeTagInput = (event) => changeTagInput(event.target.value);

    const handleAddTag = () => {
        setTags([...tags, inputTag]);
        changeTagInput("");
    }

    const handleTagListKeyUp = (event) => {
        if (event.key === "Enter") {
            handleAddTag();
        }
    }

    const sendFileClick = async function() {
        setSubmitting(true);

        const accessToken = await getAccessTokenSilently();
        const selectedPhoneList = contactsForEmail
            .filter(c => c.checked)
            .map(c => c.phone)
            .filter(Boolean);
        const formData = new FormData();
        formData.append("formFile", file);
        formData.append("fileName", fileName);
        formData.append("fileType", fileType);
        formData.append("tags", JSON.stringify(tags));
        formData.append("isBanner", featureConfigs.isBanner);
        formData.append("isFeatured", featureConfigs.isFeatured);
        formData.append("category", contentCategory);
        formData.append("phoneList", selectedPhoneList.join(","));
        formData.append("emailList", JSON.stringify(contactsForEmail.filter(c => c.checked).map(c => c.email)));
        formData.append("allowedAffiliations", JSON.stringify(allowedAffiliations));

        var uploadedcontent = await uploadContent(accessToken, formData, id);
        if (!uploadedcontent.error) {
            toast.success(`content ${id ? "updated" : "created"}!`);
            id && resetState();
        }
        else {
            toast.error("there was an issue!");
        }

        setSubmitting(false);
    }

    const deleteContent = async () => {
        setDeleting(true);
        const accessToken = await getAccessTokenSilently();
        let contentDeleted = await deleteContentById(accessToken, id); 
               
        if (!contentDeleted.error) {
            toast.success(`Content Deleted!`);
            resetState();
            window.location = '/Content/All';
        }
        else {
            toast.error(`Content deletion failed: ${contentDeleted.error.message}`);
        }

        setDeleting(false);
    } 
    
    const handleRemoveTagClick = (event) => {
        let tagIndex = event.target.parentElement.dataset.index;

        // remove the tag at the given index
        let newTags = [...tags];
        newTags.splice(tagIndex, 1);

        setTags(newTags);
    }

    function handleNotificationChange() {
        setisVisible(!isVisible);
        setContactsForEmail(contactsForEmail.map(c => 
            ({...c, checked: !isVisible})
        ));
    }

    const checkAllChangeHandler = (allChecked) => {
        const newContacts = [...contactsForEmail];
        setContactsForEmail(newContacts.map(c => ({...c, checked : allChecked})));
    }

    const handleChangeCheckBox = (e) => {
        const contactId = e.target.dataset.contactid
        let updatedContactList = contactsForEmail.map(c => c.id === contactId ? {...c, checked: !c.checked} : c);
        setContactsForEmail(updatedContactList);
    };

    const currentTagList = tags.map((t, i) => 
        <div data-index={i} key={i} className="searchTagToAdd tag-temp">
            {t}
            <i onClick={handleRemoveTagClick} className="fa-regular fa-xmark"></i>
        </div>
    )

    return (
        <div className="admin-panel-outer">
            <div className="admin-panel-header">{content ? "Edit" : "Add"} Content</div>
            <input onChange={handleFileChange} type="file" />
            <input className="content-upload-textinput" type="text" placeholder="content description" onChange={handleFileNameChange} value={fileName} />
            <div className="content-upload-tags">
                <input className="content-upload-textinput" type="text" placeholder="add a search tag" value={inputTag} onKeyUp={handleTagListKeyUp} onChange={handleChangeTagInput} />
                <button className="btn btn2" style={{fontSize: "15px", padding: "5px 18px"}} onClick={handleAddTag}>Add tag <i className="fa-regular fa-plus"></i></button>
                <div className="content-tags-runninglist">
                    {currentTagList.length > 0 && currentTagList}
                </div>
            </div>
            <div style={{display: "flex", gap: "10px", width: "100%", justifyContent: "space-between", alignItems: "center", marginBottom: "10px"}}>
                <label className="content-upload-label" style={{color: "white"}}>Content Category</label>
                <Select 
                    options={contentCategories.map(c => ({value: c, label: c}))}
                    placeholder="Select category"
                    value={contentCategory ? {value: contentCategory, label: contentCategory} : null}
                    onChange={(val) => setContentCategory(val.value)}
                    theme={(theme) => ({
                        ...theme,
                        borderRadius: 0,
                        colors: {
                            ...theme.colors,
                            primary25: 'gray',
                            primary: 'gray',
                            neutral0: "#222222",
                            neutral5: "white",
                            neutral10: "white",
                            neutral20: "gray"
                        },
                    })}
                    styles={{
                        container: (provided) => ({
                            ...provided,
                            maxWidth: "200px",
                            width: "50%"
                        }),
                        singleValue: (baseStyles) => ({
                            ...baseStyles,
                            fontSize: "15px",
                            fontWeight: "400",
                            color: "white"
                        }),
                        multiValueRemove: (baseStyles) => ({
                            ...baseStyles,
                            color: "black",
                        })
                    }}
                />
            </div>
            <CreateContactField 
                value={featureConfigs.isFeatured} 
                type="checkbox" 
                id="isFeaturedContentField" 
                handleValueChange={handleOtherFieldChange} 
                propName="isFeatured" 
                label="Feature this content on the front page?" 
                style={{justifyContent: "space-between", maxWidth: "unset"}}
            />
            <CreateContactField 
                value={featureConfigs.isBanner} 
                type="checkbox"
                id="isBannerContentField" 
                handleValueChange={handleOtherFieldChange} 
                propName="isBanner" 
                label="Make this item the front page banner?" 
                style={{justifyContent: "space-between", maxWidth: "unset"}}
            />
            <div style={{display: "flex", gap: "10px", width: "100%", justifyContent: "space-between", alignItems: "center", marginBottom: "10px"}}>
                <label className="content-upload-label" style={{color: "white"}}>Restrict Access</label>
                <Select 
                    options={affiliations?.data?.map(c => ({value: c.id, label: c.name}))}
                    placeholder="Select affiliations"
                    isMulti={true}
                    closeMenuOnSelect={false}
                    hideSelectedOptions={false}
                    components={{ Option: CustomOption, ValueContainer: CustomValueContainer }}
                    value={allowedAffiliations?.map(a => ({value: a.id, label: a.name}))}
                    onChange={(val) => handleAffiliationChange(val)}
                    theme={(theme) => ({
                        ...theme,
                        borderRadius: 0,
                        colors: {
                            ...theme.colors,
                            primary25: 'gray',
                            primary: 'gray',
                            neutral0: "#222222",
                            neutral5: "white",
                            neutral10: "white",
                            neutral20: "gray"
                        },
                    })}
                    styles={{
                        container: (provided) => ({
                            ...provided,
                            maxWidth: "200px",
                            width: "50%"
                        }),
                        singleValue: (baseStyles) => ({
                            ...baseStyles,
                            fontSize: "15px",
                            fontWeight: "400",
                            color: "white"
                        }),
                        multiValueRemove: (baseStyles) => ({
                            ...baseStyles,
                            color: "black",
                        })
                    }}
                />
            </div>
            <CreateContactField 
                type="checkbox"
                id="sendNotificationContentField" 
                handleValueChange={handleNotificationChange} 
                propName="sendNotification" 
                label="Notify Members" 
                style={{justifyContent: "space-between", maxWidth: "unset"}}
            />
            
            { isVisible && 
                <ContactCheckboxList 
                    contacts={contactsForEmail}
                    loading={contactsList.status == "loading"}
                    handleChangeCheckBox={handleChangeCheckBox}
                    handleCheckAllChange={checkAllChangeHandler}
                />
            }

            {/* <button style={{width: "100%"}} className="btn btn1" onClick={sendFileClick}>
                {isSubmitting ? <Loader /> : <><span>Upload</span><i className="fa-regular fa-arrow-right"></i></> }
            </button> */}

            <div className="contact-update-delete-row" style={{width: "100%", marginTop:"12px"}}>
                {
                    editMode &&
                    <button id="deleteContactBtn" className="btn btnDelete" onClick={() => setShowModal(true)} >
                        <i className="fa-solid fa-trash"></i><span>Delete</span>
                    </button>
                }
              
                <button className="btn btn1" onClick={sendFileClick} >
                    {isSubmitting ? <Loader /> : <><span>Upload</span><i className="fa-regular fa-arrow-right"></i></> }
                </button>
            </div>

            <AreYouSureModal onCompleteAction={deleteContent} handleCancelAction={() => setShowModal(false)} loadingState={isDeleting} isVisible={showModal}/>

        </div>
    )
}